import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  healthService, 
  BasicHealthResponse, 
  DetailedHealthResponse, 
  HealthSummary,
  HealthStatusLevel,
  HealthPollingConfig
} from '../services/healthService';

export interface UseHealthStatusOptions {
  /** Enable automatic polling */
  enablePolling?: boolean;
  /** Polling interval in milliseconds */
  pollingInterval?: number;
  /** Fetch detailed health on mount */
  fetchDetailed?: boolean;
  /** Fetch summary on mount */
  fetchSummary?: boolean;
  /** Enable background polling for real-time updates */
  enableBackgroundPolling?: boolean;
  /** Custom polling configuration */
  pollingConfig?: Partial<HealthPollingConfig>;
  /** Callback for status changes */
  onStatusChange?: (status: HealthStatusLevel) => void;
}

export interface UseHealthStatusReturn {
  /** Basic health status */
  basicHealth: BasicHealthResponse | null;
  /** Detailed health status */
  detailedHealth: DetailedHealthResponse | null;
  /** Health summary */
  healthSummary: HealthSummary | null;
  /** Current logo status */
  logoStatus: HealthStatusLevel;
  /** CSS class for logo status */
  logoStatusClass: string;
  /** Human-readable status message */
  statusMessage: string;
  /** Whether system is healthy */
  isSystemHealthy: boolean;
  /** Whether health check is in progress */
  isLoading: boolean;
  /** Last error if any */
  error: string | null;
  /** Polling status information */
  pollingStatus: {
    isActive: boolean;
    currentInterval: number;
    failureCount: number;
    lastStatusChange: number;
    nextCheckIn: number;
  };
  /** Manually refresh health status */
  refreshHealth: () => Promise<void>;
  /** Manually refresh detailed health */
  refreshDetailed: () => Promise<void>;
  /** Manually refresh health summary */
  refreshSummary: () => Promise<void>;
  /** Start background polling */
  startPolling: () => void;
  /** Stop background polling */
  stopPolling: () => void;
}

export const useHealthStatus = (options: UseHealthStatusOptions = {}): UseHealthStatusReturn => {
  const {
    enablePolling = true,
    pollingInterval = 30000, // 30 seconds
    fetchDetailed = false,
    fetchSummary = true,
    enableBackgroundPolling = true,
    pollingConfig,
    onStatusChange,
  } = options;

  const [basicHealth, setBasicHealth] = useState<BasicHealthResponse | null>(null);
  const [detailedHealth, setDetailedHealth] = useState<DetailedHealthResponse | null>(null);
  const [healthSummary, setHealthSummary] = useState<HealthSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollingStatus, setPollingStatus] = useState(healthService.getPollingStatus());

  // Refs for cleanup
  const statusListenerRef = useRef<(() => void) | null>(null);
  const pollingStatusIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Configure health service on mount
  useEffect(() => {
    if (pollingConfig) {
      healthService.configure(pollingConfig);
    }
    
    // Set custom interval if provided
    if (pollingInterval && pollingInterval !== 30000) {
      healthService.setCheckInterval(pollingInterval);
    }
  }, [pollingConfig, pollingInterval]);

  // Set up status change listener
  useEffect(() => {
    const handleStatusChange = (status: HealthStatusLevel) => {
      // Update the current logo status and trigger re-render
      setHealthSummary(healthService.getCachedHealthSummary());
      setBasicHealth(healthService.getCachedBasicStatus());
      
      // Call user-provided callback
      if (onStatusChange) {
        onStatusChange(status);
      }
    };

    statusListenerRef.current = healthService.addStatusListener(handleStatusChange);

    return () => {
      if (statusListenerRef.current) {
        statusListenerRef.current();
        statusListenerRef.current = null;
      }
    };
  }, [onStatusChange]);

  // Set up polling status updates
  useEffect(() => {
    const updatePollingStatus = () => {
      setPollingStatus(healthService.getPollingStatus());
    };

    // Update polling status every 5 seconds
    pollingStatusIntervalRef.current = setInterval(updatePollingStatus, 5000);
    updatePollingStatus(); // Initial update

    return () => {
      if (pollingStatusIntervalRef.current) {
        clearInterval(pollingStatusIntervalRef.current);
        pollingStatusIntervalRef.current = null;
      }
    };
  }, []);

  // Refresh basic health
  const refreshHealth = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const health = await healthService.getBasicHealth();
      setBasicHealth(health);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch health status';
      setError(errorMessage);
      console.error('Health check failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh detailed health
  const refreshDetailed = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const detailed = await healthService.getDetailedHealth();
      setDetailedHealth(detailed);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch detailed health status';
      setError(errorMessage);
      console.error('Detailed health check failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh health summary
  const refreshSummary = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const summary = await healthService.getHealthSummary();
      setHealthSummary(summary);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch health summary';
      setError(errorMessage);
      console.error('Health summary check failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Start background polling
  const startPolling = useCallback(() => {
    healthService.startBackgroundPolling();
    setPollingStatus(healthService.getPollingStatus());
  }, []);

  // Stop background polling
  const stopPolling = useCallback(() => {
    healthService.stopBackgroundPolling();
    setPollingStatus(healthService.getPollingStatus());
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    const fetchInitialHealth = async () => {
      setIsLoading(true);
      
      try {
        // Always fetch basic health
        await refreshHealth();
        
        // Fetch detailed if requested
        if (fetchDetailed) {
          await refreshDetailed();
        }
        
        // Fetch summary if requested
        if (fetchSummary) {
          await refreshSummary();
        }
      } catch (err) {
        console.error('Initial health fetch failed:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialHealth();
  }, [refreshHealth, refreshDetailed, refreshSummary, fetchDetailed, fetchSummary]);

  // Set up background polling
  useEffect(() => {
    if (enableBackgroundPolling) {
      healthService.startBackgroundPolling();
    }

    return () => {
      if (enableBackgroundPolling) {
        healthService.stopBackgroundPolling();
      }
    };
  }, [enableBackgroundPolling]);

  // Legacy polling support (fallback if background polling is disabled)
  useEffect(() => {
    if (!enablePolling || enableBackgroundPolling) return;

    const interval = setInterval(async () => {
      try {
        // Always poll basic health
        await refreshHealth();
        
        // Poll summary if it was initially requested
        if (fetchSummary) {
          await refreshSummary();
        }
      } catch (err) {
        console.error('Health polling failed:', err);
      }
    }, pollingInterval);

    return () => clearInterval(interval);
  }, [enablePolling, enableBackgroundPolling, pollingInterval, refreshHealth, refreshSummary, fetchSummary]);

  // Derived values
  const logoStatus = healthService.getLogoStatus();
  const logoStatusClass = healthService.getLogoStatusClass();
  const statusMessage = healthService.getStatusMessage();
  const isSystemHealthy = healthService.isSystemHealthy();

  return {
    basicHealth,
    detailedHealth,
    healthSummary,
    logoStatus,
    logoStatusClass,
    statusMessage,
    isSystemHealthy,
    isLoading,
    error,
    pollingStatus,
    refreshHealth,
    refreshDetailed,
    refreshSummary,
    startPolling,
    stopPolling,
  };
}; 